import { NavLink, useLocation } from 'react-router-dom';
import { Home, ListChecks, BookHeart, MessageCircle, Settings, Sun, Moon } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import { useUser } from '../contexts/UserContext';

export const Navigation = () => {
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { getAgeConfig } = useUser();
  const config = getAgeConfig();

  const navItems = [
    { path: '/dashboard', icon: Home, label: 'Hoy' },
    { path: '/habits', icon: ListChecks, label: 'Hábitos' },
    { path: '/diary', icon: BookHeart, label: 'Diario' },
    { path: '/chat', icon: MessageCircle, label: 'Conversar' },
    { path: '/settings', icon: Settings, label: 'Ajustes' },
  ];

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 h-full w-20 flex-col items-center py-8 glass z-50">
        <div className="mb-8">
          <div className="w-10 h-10 rounded-full bg-[#FF7A59] flex items-center justify-center">
            <span className="text-white font-bold text-lg">M</span>
          </div>
        </div>
        
        <nav className="flex-1 flex flex-col items-center gap-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className="relative p-3 rounded-xl transition-colors duration-200"
              >
                {isActive && (
                  <motion.div
                    layoutId="nav-indicator"
                    className="absolute inset-0 bg-[#FF7A59]/10 dark:bg-[#FF7A59]/20 rounded-xl"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  />
                )}
                <Icon 
                  className={`w-6 h-6 relative z-10 transition-colors duration-200 ${
                    isActive 
                      ? 'text-[#FF7A59]' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                />
              </NavLink>
            );
          })}
        </nav>
        
        <button
          onClick={toggleTheme}
          className="p-3 rounded-xl hover:bg-muted transition-colors duration-200"
          data-testid="theme-toggle-desktop"
          aria-label="Toggle theme"
        >
          {theme === 'dark' ? (
            <Sun className="w-5 h-5 text-muted-foreground" />
          ) : (
            <Moon className="w-5 h-5 text-muted-foreground" />
          )}
        </button>
      </aside>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 glass z-50 px-4 py-2 safe-area-bottom">
        <div className="flex items-center justify-around">
          {navItems.slice(0, 4).map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`nav-mobile-${item.label.toLowerCase()}`}
                className="relative flex flex-col items-center py-2 px-4"
              >
                {isActive && (
                  <motion.div
                    layoutId="nav-indicator-mobile"
                    className="absolute -top-1 w-1 h-1 rounded-full bg-[#FF7A59]"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  />
                )}
                <Icon 
                  className={`w-6 h-6 transition-colors duration-200 ${
                    isActive 
                      ? 'text-[#FF7A59]' 
                      : 'text-muted-foreground'
                  }`}
                />
                <span className={`text-xs mt-1 ${
                  isActive ? 'text-[#FF7A59]' : 'text-muted-foreground'
                }`}>
                  {item.label}
                </span>
              </NavLink>
            );
          })}
          
          <button
            onClick={toggleTheme}
            className="flex flex-col items-center py-2 px-4"
            data-testid="theme-toggle-mobile"
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? (
              <Sun className="w-6 h-6 text-muted-foreground" />
            ) : (
              <Moon className="w-6 h-6 text-muted-foreground" />
            )}
            <span className="text-xs mt-1 text-muted-foreground">Tema</span>
          </button>
        </div>
      </nav>
    </>
  );
};

export default Navigation;
